import React from "react";
import styled from "styled-components";
import { FiAlertTriangle, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { MdLightbulbOutline } from "react-icons/md";
import { colors } from "./colors";
import { formatCompact, formatSignedPercent, pctChange } from "./format";
import { measureOf } from "./drops";
import TrendChart from "./TrendChart";
import PageChangeList from "./PageChangeList";
import { SectionTitle, SectionNote } from "./Section";

const CHECK_ICONS = {
    fail: { Icon: FiXCircle, color: colors.critical, label: "Failing" },
    warn: { Icon: FiAlertTriangle, color: colors.warning, label: "Warning" },
    pass: { Icon: FiCheckCircle, color: colors.positive, label: "Passing" },
};
const CHECK_ORDER = { fail: 0, warn: 1, pass: 2 };

const metricsOf = (drop) => {
    const positionDiff = drop.position.after - drop.position.before;
    return [
        { label: "Clicks / wk", before: formatCompact(drop.clicks.before), after: formatCompact(drop.clicks.after), delta: formatSignedPercent(pctChange(drop.clicks.before, drop.clicks.after)), bad: drop.clicks.after < drop.clicks.before },
        { label: "Impressions / wk", before: formatCompact(drop.impressions.before), after: formatCompact(drop.impressions.after), delta: formatSignedPercent(pctChange(drop.impressions.before, drop.impressions.after)), bad: drop.impressions.after < drop.impressions.before * 0.97 },
        { label: "CTR", before: `${drop.ctr.before.toFixed(2)}%`, after: `${drop.ctr.after.toFixed(2)}%`, delta: formatSignedPercent(pctChange(drop.ctr.before, drop.ctr.after)), bad: drop.ctr.after < drop.ctr.before * 0.97 },
        { label: "Avg. position", before: drop.position.before.toFixed(1), after: drop.position.after.toFixed(1), delta: `${positionDiff > 0 ? "+" : "−"}${Math.abs(positionDiff).toFixed(1)}`, bad: positionDiff > 0.5 },
    ];
};

export default function PanelBody({ drop, measure, relatedEventTitle }) {
    const { trendStart } = drop;
    const checks = [...drop.diagnostics].sort((a, b) => CHECK_ORDER[a.status] - CHECK_ORDER[b.status]);
    const failing = checks.filter((check) => check.status === "fail").length;

    return (
        <Body>
            <section aria-labelledby="trend-heading">
                <Between>
                    <SectionTitle id="trend-heading">Daily {measureOf(measure).unit}</SectionTitle>
                    {relatedEventTitle && <Aside>Aligned with {relatedEventTitle}</Aside>}
                </Between>
                <Spaced><TrendChart values={drop.byMeasure[measure].trend} unit={measureOf(measure).unit} breakIndex={drop.trendBreak} trendStart={trendStart} changeDates={drop.pageChanges.map((change) => change.date)} /></Spaced>
                <Metrics>
                    {metricsOf(drop).map((metric) => (
                        <Metric key={metric.label}>
                            <dt>{metric.label}</dt>
                            <dd><strong>{metric.after}</strong><From>from {metric.before}</From></dd>
                            <Delta $bad={metric.bad}>{metric.delta}</Delta>
                        </Metric>
                    ))}
                </Metrics>
            </section>

            <PageChangeList changes={drop.pageChanges} trendStart={trendStart} />

            <section aria-labelledby="diagnosis-heading">
                <SectionTitle id="diagnosis-heading">
                    Diagnosis
                    <SectionNote>{failing} failing {failing === 1 ? "check" : "checks"} of {checks.length}</SectionNote>
                </SectionTitle>
                <Checks>
                    {checks.map((check) => {
                        const { Icon, color, label } = CHECK_ICONS[check.status];
                        return (
                            <Check key={check.label}>
                                <Icon aria-label={label} style={{ color, flexShrink: 0, marginTop: 2 }} />
                                <div>
                                    <CheckLabel>{check.label}</CheckLabel>
                                    <CheckDetail>{check.detail}</CheckDetail>
                                </div>
                            </Check>
                        );
                    })}
                </Checks>
            </section>

            <section aria-labelledby="queries-heading">
                <SectionTitle id="queries-heading">Most affected queries</SectionTitle>
                <Queries>
                    <thead>
                        <tr>
                            <th scope="col">Query</th>
                            <th scope="col" style={{ textAlign: "right" }}>Clicks</th>
                            <th scope="col" style={{ textAlign: "right", paddingLeft: 16 }}>Position</th>
                        </tr>
                    </thead>
                    <tbody>
                        {drop.topQueries.map((query) => (
                            <tr key={query.query}>
                                <td>{query.query}</td>
                                <td style={{ textAlign: "right" }}><Faint>{formatCompact(query.clicksBefore)} → </Faint><strong>{formatCompact(query.clicksAfter)}</strong></td>
                                <td style={{ textAlign: "right", paddingLeft: 16 }}>
                                    <Faint>{query.positionBefore.toFixed(1)} → </Faint>
                                    <strong style={{ color: query.positionAfter - query.positionBefore > 1 ? colors.criticalInk : colors.ink }}>{query.positionAfter.toFixed(1)}</strong>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Queries>
            </section>

            <NextStep aria-labelledby="next-step-heading">
                <SectionTitle id="next-step-heading" as="h3" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <MdLightbulbOutline aria-hidden style={{ color: colors.accent }} />
                    Recommended next step
                </SectionTitle>
                <p>{drop.nextStep}</p>
            </NextStep>
        </Body>
    );
}

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
    padding: 20px 24px;
`;

const Between = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
`;

const Aside = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${colors.muted};
`;

const Spaced = styled.div`
    margin-top: 8px;
`;

const Metrics = styled.dl`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    overflow: hidden;
    margin: 16px 0 0;
    border-radius: 8px;
    border: 1px solid ${colors.line};
    @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
`;

const Metric = styled.div`
    padding: 10px 12px;
    border-left: 1px solid ${colors.line};
    &:first-child { border-left: none; }
    dt { font-size: 12px; color: ${colors.muted}; }
    dd { margin: 4px 0 0; font-variant-numeric: tabular-nums; }
    strong { font-size: 14px; font-weight: 600; color: ${colors.ink}; }
    @media (max-width: 600px) {
        &:nth-child(odd) { border-left: none; }
        &:nth-child(n + 3) { border-top: 1px solid ${colors.line}; }
    }
`;

const From = styled.span`
    margin-left: 4px;
    font-size: 12px;
    color: ${colors.subtle};
`;

const Delta = styled.dd`
    font-size: 12px;
    font-weight: 500;
    color: ${({ $bad }) => ($bad ? colors.criticalInk : colors.muted)};
`;

const Checks = styled.ul`
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
`;

const Check = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 0;
    border-top: 1px solid ${colors.line};
    &:first-child { border-top: none; }
`;

const CheckLabel = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.ink};
`;

const CheckDetail = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${colors.muted};
`;

const Queries = styled.table`
    width: 100%;
    margin-top: 12px;
    border-collapse: collapse;
    text-align: left;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    th { padding-bottom: 8px; font-size: 12px; font-weight: 500; color: ${colors.muted}; border-bottom: 1px solid ${colors.line}; }
    td { padding: 8px 12px 8px 0; color: ${colors.ink}; white-space: nowrap; border-bottom: 1px solid ${colors.line}; }
    td:first-child { white-space: normal; }
    tr:last-child td { border-bottom: none; }
    strong { font-weight: 500; }
`;

const Faint = styled.span`
    color: ${colors.subtle};
`;

const NextStep = styled.section`
    border-radius: 8px;
    padding: 16px;
    background: ${colors.accentSoft};
    p { margin: 6px 0 0; font-size: 13px; line-height: 1.6; color: ${colors.ink}; }
`;
